import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Breadcrumb from '../../../component/common/Breadcrumb';
import RecipeDetailsTab from '../../../component/HealthComponent/RecipeForm/RecipeDetailsTab';
import StepsTab from '../../../component/HealthComponent/RecipeForm/StepsTab';
import NutritionTab from '../../../component/HealthComponent/RecipeForm/NutritionTab';
import AddOnsTab from '../../../component/HealthComponent/RecipeForm/AddOnsTab';
import { useRecipeCreation, RecipeCreationProvider } from '../../../context/RecipeCreationContext';
import { getLocalStorageItem, successToast, errorToast } from '../../../utils/helper';
import { getRecipeById } from '../../../api/healthAPI';

const NewRecipeComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const propsData = location?.state;
  const id = new URLSearchParams(window.location.search).get('id');
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));

  // Use recipe creation context
  const {
    recipeData,
    recipeId,
    errors,
    isLoading,
    updateRecipeData,
    submitRecipe,
    saveCurrentStep,
    goToNextStep,
    goToPreviousStep,
    validateCurrentStep,
    setErrors,
    currentStep
  } = useRecipeCreation();

  // View mode state
  const [isView, setIsView] = useState(false);
  // Breadcrumb
  const [pages] = useState([
    {
      name: 'Health Dashboard',
      href: `${userData?.slug ? `/${userData.slug}` : ''}/health/dashboard`
    },
    { name: 'Recipes', href: `${userData?.slug ? `/${userData.slug}` : ''}/health/recipes` },
    { name: id ? 'Edit Recipe' : 'New Recipe', current: true }
  ]);

  // Handle save and continue for steps 1-3
  const handleSaveAndContinue = async (e) => {
    e.preventDefault();

    try {
      const response = await saveCurrentStep(currentStep, 'draft');

      if (response?.success) {
        // Move to next step if save was successful
        goToNextStep();
      }
    } catch (err) {
      console.error('Error saving recipe:', err);
      errorToast(err.message || 'Failed to save recipe. Please try again.');
    }
  };

  // Handle publish for step 4
  const handlePublish = async (e) => {
    e.preventDefault();

    try {
      const response = await saveCurrentStep(4, 'published');

      if (response?.success) {
        navigate(`${userData?.slug ? `/${userData.slug}` : ''}/health/recipes`);
      }
    } catch (err) {
      console.error('Error publishing recipe:', err);
      errorToast(err.message || 'Failed to publish recipe. Please try again.');
    }
  };

  // Handle save as draft for step 4
  const handleSaveAsDraft = async (e) => {
    e.preventDefault();

    try {
      const response = await saveCurrentStep(4, 'draft');

      if (response?.success) {
        successToast('Recipe saved as draft successfully!');
        navigate(`${userData?.slug ? `/${userData.slug}` : ''}/health/recipes`);
      }
    } catch (err) {
      console.error('Error saving draft:', err);
      errorToast(err.message || 'Failed to save draft. Please try again.');
    }
  };

  const isEditing = !!(recipeId || id);

  // Handle cancel
  const handleCancel = () => {
    navigate(`${userData?.slug ? `/${userData.slug}` : ''}/health/recipes`);
  };

  // Update mode only — save current step via API without advancing
  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await saveCurrentStep(currentStep, 'draft');
    } catch (err) {
      errorToast(err.message || 'Failed to update recipe.');
    }
  };

  // Tab navigation handler — in update mode, saves current step via API before switching
  const handleTabClick = async (targetStep) => {
    if (targetStep === currentStep) return;
    const isEditing = !!(recipeId || id);
    if (isEditing) {
      const response = await saveCurrentStep(currentStep, 'draft');
      if (response?.success) {
        updateRecipeData({ currentStep: targetStep });
      }
    } else {
      updateRecipeData({ currentStep: targetStep });
    }
  };

  const handleTabKey = (e, step) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTabClick(step);
    }
  };

  // Fetch recipe data for editing
  const fetchRecipeData = async (recipeId) => {
    try {
      const response = await getRecipeById(recipeId);
      console.log('Fetched recipe data:', response);

      if (response?.data) {
        const recipe = response.data;

        // Add unique IDs to items if they don't have them (for UI tracking)
        const ingredientsWithIds =
          recipe.ingredients?.map((item) => ({
            ...item,
            id: item._id || Date.now() + Math.random()
          })) || [];

        const stepsWithIds =
          recipe.preparation_steps?.map((item) => ({
            ...item,
            id: item._id || Date.now() + Math.random()
          })) || [];

        const nutritionWithIds =
          recipe.nutritional_value?.map((item) => ({
            ...item,
            id: item._id || Date.now() + Math.random()
          })) || [];

        // Update context with fetched data
        updateRecipeData({
          recipeId: recipe._id || recipe.id,
          title: recipe.title || '',
          cooking_duration: recipe.cooking_duration || 25,
          servings: recipe.servings || 2,
          difficulty: recipe.difficulty || '',
          category: recipe.category || '',
          ingredients: ingredientsWithIds,
          preparation_steps: stepsWithIds,
          nutritional_value: nutritionWithIds,
          chefs_note: recipe.notes || '',
          image_url: recipe.image_url || '',
          cover_image_preview: recipe.image_url || null,
          // Set current step from backend, but don't exceed step 4 (final step)
          currentStep: Math.min(recipe.current_step + 1 || 1, 4)
        });

        successToast('Recipe data loaded successfully');
      }
    } catch (err) {
      console.error('Error fetching recipe data:', err);
      errorToast('Failed to load recipe data');
    }
  };

  // Load data if editing
  useEffect(() => {
    const recipeId = id || propsData?._id;
    if (recipeId) {
      if (propsData?.action === 'view') {
        setIsView(true);
      }
      fetchRecipeData(recipeId);
    }
  }, [id, propsData]);

  return (
    <div className="relative lg:mb-[45px]">
      <Helmet>
        <meta charSet="utf-8" />
        <title>{id ? 'Edit Recipe' : 'New Recipe'} | Shoorah Admin</title>
      </Helmet>

      <Breadcrumb pageList={pages} className="lg:mt-8" />

      {/* Tab Navigation */}
      <nav className="mt-6 rounded-t-lg" role="tablist" aria-label="Recipe tabs">
        <ul className="flex gap-6">
          <li>
            <button
              type="button"
              role="tab"
              aria-selected={currentStep === 1}
              tabIndex={currentStep === 1 ? 0 : -1}
              onClick={() => handleTabClick(1)}
              onKeyDown={(e) => handleTabKey(e, 1)}
              className={`px-1 pb-3 pt-4 text-base cursor-pointer ${
                currentStep === 1 ? 'text-black font-medium' : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              Details
            </button>
          </li>
          <li>
            <button
              type="button"
              role="tab"
              aria-selected={currentStep === 2}
              tabIndex={currentStep === 2 ? 0 : -1}
              onClick={() => handleTabClick(2)}
              onKeyDown={(e) => handleTabKey(e, 2)}
              className={`px-1 pb-3 pt-4 text-base cursor-pointer ${
                currentStep === 2 ? 'text-black font-medium' : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              Steps
            </button>
          </li>
          <li>
            <button
              type="button"
              role="tab"
              aria-selected={currentStep === 3}
              tabIndex={currentStep === 3 ? 0 : -1}
              onClick={() => handleTabClick(3)}
              onKeyDown={(e) => handleTabKey(e, 3)}
              className={`px-1 pb-3 pt-4 text-base cursor-pointer ${
                currentStep === 3 ? 'text-black font-medium' : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              Nutrition
            </button>
          </li>
          <li>
            <button
              type="button"
              role="tab"
              aria-selected={currentStep === 4}
              tabIndex={currentStep === 4 ? 0 : -1}
              onClick={() => handleTabClick(4)}
              onKeyDown={(e) => handleTabKey(e, 4)}
              className={`px-1 pb-3 pt-4 text-base cursor-pointer ${
                currentStep === 4 ? 'text-black font-medium' : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              Add-ons
            </button>
          </li>
        </ul>
      </nav>

      {/* Tab Content */}
      <div className="mt-6 space-y-6">
        {currentStep === 1 && (
          <div role="tabpanel" aria-labelledby="details-tab">
            <RecipeDetailsTab isView={isView} />
          </div>
        )}

        {currentStep === 2 && (
          <div role="tabpanel" aria-labelledby="steps-tab">
            <StepsTab isView={isView} />
          </div>
        )}

        {currentStep === 3 && (
          <div role="tabpanel" aria-labelledby="nutrition-tab">
            <NutritionTab isView={isView} />
          </div>
        )}

        {currentStep === 4 && (
          <div role="tabpanel" aria-labelledby="addons-tab">
            <AddOnsTab isView={isView} />
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {!isView && (
        <div className="mt-8 flex justify-start gap-4 dark:bg-shoorah-darkBgTabColor rounded-lg">
          {/* Steps 1-3: Save & Continue button */}
          {currentStep < 4 && (
            <>
              {isEditing ? (
                <>
                  <button
                    type="button"
                    onClick={handleUpdate}
                    disabled={isLoading}
                    className="btn-primary"
                  >
                    {isLoading ? 'Updating...' : 'Update'}
                  </button>
                  <button
                    type="button"
                    onClick={goToNextStep}
                    disabled={isLoading}
                    className="px-8 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-shoorah-darkBgTabColor hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleSaveAndContinue}
                  disabled={isLoading}
                  className="btn-primary"
                >
                  {isLoading ? 'Saving...' : 'Save & Continue'}
                </button>
              )}
              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className="px-8 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-shoorah-darkBgTabColor hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </>
          )}

          {/* Step 4: Publish and Save as Draft buttons */}
          {currentStep === 4 && (
            <>
              <button
                type="button"
                onClick={handleSaveAsDraft}
                disabled={isLoading}
                className="px-8 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-shoorah-darkBgTabColor hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : 'Save as Draft'}
              </button>
              <button
                type="button"
                onClick={handlePublish}
                disabled={isLoading}
                className="btn-primary"
              >
                {isLoading ? 'Publishing...' : 'Publish'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className="px-8 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-shoorah-darkBgTabColor hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Wrap the component with the RecipeCreationProvider
const NewRecipe = () => {
  return (
    <RecipeCreationProvider>
      <NewRecipeComponent />
    </RecipeCreationProvider>
  );
};

export default NewRecipe;
